import { useRef, useEffect, useCallback } from 'react';
import useProductTypeLabels from '@hooks/useProductTypeLabels';
import { isString, minBy } from 'lodash';
import { productLoadState } from '@enums';

export default function useProductTypeHelpers(productTypes) {
  // Saved ref of current product types to avoid re-renders
  const productTypesRef = useRef(getProductTypesRef(productTypes));

  useEffect(() => {
    productTypesRef.current = getProductTypesRef(productTypes);
  }, [productTypes]);

  // Stores which product types have been requested
  // for each loading state. This helps us avoid
  // fetching products multiple times
  const createRequestPhaseArray = useCallback((requestPhases, shouldQueue) => {
    const arr = new Array(requestPhases?.length ?? 0).fill(
      productLoadState.NOT_STARTED
    );

    if (shouldQueue && arr.length) {
      arr[0] = productLoadState.QUEUED;
    }

    return arr;
  }, []);

  const productIsEnabled = useCallback(
    (productTypeID, loanType, productTypes) => {
      productTypes = productTypes ?? productTypesRef.current;

      if (!productTypes || !productTypes[loanType]?.isEnabled) {
        return false;
      }

      const products = loanType
        ? productTypes[loanType]?.products
        : getAllProductTypes(productTypes);

      const product = products?.find(p => p.productTypeID === productTypeID);

      return product?.isEnabled && productTypes[product.loanType].isEnabled;
    },
    []
  );

  const createLoadingState = useCallback(
    (requestPhases, productTypes) => {
      productTypes = productTypes ?? productTypesRef.current ?? {};

      if (!productTypes || (!productTypes.fixed && !productTypes.adjustable)) {
        return [];
      }

      return getAllProductTypes(productTypes)
        .map(({ productTypeID, isEnabled, loanType, label }) => {
          return productTypeID <= 0 || !productTypes[loanType]
            ? null
            : {
              productTypeID,
              label,
              loanType,
              totalRequestPhases: requestPhases.length,
              requestPhases: createRequestPhaseArray(
                requestPhases,
                isEnabled && productTypes[loanType].isEnabled
              )
            };
        })
        .filter(p => p);
    },
    [createRequestPhaseArray]
  );

  const updateLoadingState = useCallback(
    (
      loadState,
      currentLoadingState,
      productTypeIDs = [],
      requestPhaseIndex = 0
    ) => {
      const totalRequestPhases =
        currentLoadingState?.[0]?.requestPhases.length ?? 0;

      if (!isLoadState(loadState) || requestPhaseIndex >= totalRequestPhases) {
        console.error('Invalid loading state or request phase');
        return currentLoadingState;
      }

      return currentLoadingState?.map(state => {
        if (!productTypeIDs.find(id => id === state.productTypeID)) {
          return state;
        }

        const newState = { ...state };
        const prevState = newState.requestPhases[requestPhaseIndex];

        if (
          loadState === productLoadState.LOADING &&
          prevState === productLoadState.QUEUED_LOAD_MORE
        ) {
          newState.requestPhases[requestPhaseIndex] =
            productLoadState.LOADING_MORE;
        } else {
          newState.requestPhases[requestPhaseIndex] = loadState;
        }

        return newState;
      });
    },
    []
  );

  const getEnabledProducts = useCallback(
    (productTypes = productTypesRef.current) => {
      const { fixed, adjustable } = productTypes;

      const products = [
        fixed?.isEnabled ? fixed.products : [],
        adjustable?.isEnabled ? adjustable.products : []
      ];

      return products.flat().filter(p => p.isEnabled);
    },
    []
  );

  const getProductsToLoad = useCallback(
    (
      requestPhaseIndex,
      loadingState = [],
      onlyReturnIds = true,
      includeDisabledProducts
    ) => {
      if (!loadingState?.length || !productTypesRef.current.products) {
        return [];
      }

      const totalRequestPhases = loadingState[0].requestPhases.length;

      const productTypes = includeDisabledProducts
        ? getAllProductTypes(productTypesRef.current)
        : getEnabledProducts(productTypesRef.current);

      const products = productTypes
        .map(product => {
          const productState = loadingState.find(
            p => p.productTypeID === product.productTypeID
          );

          if (
            (!includeDisabledProducts && !product.isEnabled) ||
            !productState
          ) {
            return null;
          }

          const currentLoadState =
            productState.requestPhases[requestPhaseIndex];

          const canLoad =
            requestPhaseIndex < totalRequestPhases &&
            (currentLoadState === productLoadState.NOT_STARTED ||
              currentLoadState === productLoadState.QUEUED ||
              currentLoadState === productLoadState.QUEUED_LOAD_MORE);

          return canLoad ? product : null;
        })
        .filter(p => p)
        .sort(p => (p.isAdjustable ? -1 : 1));

      return !onlyReturnIds ? products : products.map(p => p.productTypeID);
    },
    [getEnabledProducts]
  );

  const getRequestPhaseWithProductsToLoad = useCallback(
    (loadingState, productTypes) => {
      productTypes = productTypes ?? productTypesRef.current;

      const minProduct = minBy(loadingState, product => {
        if (
          !product.requestPhases?.length ||
          !productIsEnabled(
            product.productTypeID,
            product.loanType,
            productTypes
          )
        ) {
          return null;
        }

        const index = product.requestPhases.findIndex(loadState => {
          return loadState === productLoadState.NOT_STARTED;
        });

        return index < 0 ? null : index;
      });

      return !minProduct
        ? null
        : minProduct.requestPhases.indexOf(productLoadState.NOT_STARTED);
    },
    [productIsEnabled]
  );

  // Object to hold labels for each different product type.
  // This allows us to avoid searching the productTypes
  // array for the label of every rate returned from the API
  const labels = useProductTypeLabels(productTypes);

  return {
    labels,
    productIsEnabled,
    getProductsToLoad,
    createLoadingState,
    updateLoadingState,
    getEnabledProducts,
    getAllProductTypes,
    getRequestPhaseWithProductsToLoad,
    cachedProductTypes: productTypesRef.current
  };
}

const isLoadState = (() => {
  const values = Object.values(productLoadState);

  return state => {
    return isString(state) && values.includes(state);
  };
})();

function getProductTypesRef(productTypes = {}) {
  return {
    ...productTypes,
    products: getAllProductTypes(productTypes)
  };
}

export function getAllProductTypes(productTypes = {}) {
  return [
    productTypes?.fixed?.products ?? [],
    productTypes?.adjustable?.products ?? []
  ]
    .flat()
    .filter(p => p);
}
